import React, { Component } from 'react';
import Chat from '../chat/chat';
import { t } from '../i18n';

export default class ChatFrame extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,  // 初始化为加载状态
            initialized: false,
        };
        this.chatRef = React.createRef();  // 聊天组件的ref
    }

    componentDidMount() {
        this.setState({ loading: false, initialized: true });
    }

    componentDidUpdate(prevProps) {
        // Only scroll to bottom when relevant props change, not on every update
    }

    componentWillUnmount() {
        // 清理工作
    }

    scrollToBottom = () => {
        // 直接调用Chat组件的scrollToBottom方法
        if (this.chatRef.current && this.chatRef.current.scrollToBottom) {
            this.chatRef.current.scrollToBottom();
        }
    }

    // 获取Chat组件实例
    getChatInstance = () => {
        return this.chatRef.current;
    }

    render() {
        const { isMobile, paymentCatAppId, host, fromData, conf, theme, emojiPortalContainer } = this.props;
        const { loading, initialized } = this.state;

        // 标准化isMobile值
        const isMobileBoolean = isMobile === "true" || isMobile === true;

        // 准备Chat组件的props
        const chatProps = {
            appId: paymentCatAppId,  // 将 paymentCatAppId 映射为 appId
            host,
            isMobile: isMobileBoolean,
            fromData: fromData || {},
            conf: theme || conf || {},
            emojiPortalContainer,  // 传递 emoji portal 容器
            preInitData: this.props.preInitData,  // 预初始化数据（JWT, clientId 等）
            preInitPromise: this.props.preInitPromise,  // 预初始化 promise（等待 JWT 就绪）
            // 初始子视图（'chatList' | null）：从 home「查看全部对话」进来时挂载即
            // 渲染列表，避免先画一帧默认会话再跳（见 Widget.switchToChat 注释）
            initialViewMode: this.props.initialViewMode,
            // 子视图变化回传（'chat' | 'chatList'）——返回导航要记"来路"，而从
            // 会话列表点进某条会话这一步只有 Chat 组件自己知道，widget 猜不到。
            onViewModeChange: this.props.onViewModeChange,
            // 深链目标会话（widget.html ?view=chat&chat_id=N，一路从 widget.js
            // 原样透传下来）。Chat 组件自己在 componentDidMount 的首次
            // _applyInitResponse 里读这个 prop，让第一次 loadMessages 就直接
            // 打目标会话——不再是"先挂载默认会话再 retry 切换"那条旧路径
            // （Widget._applyInitialChatId 已随这次改动退役，见其移除处的注释）。
            initialChatId: this.props.initialChatId,
        };

        return (
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    background: 'var(--lc-surface, #fff)'
                }}
            >
                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'var(--lc-bg, #f8f9fa)',
                            zIndex: 1000
                        }}
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '4px solid var(--lc-surface-2, #f3f3f3)',
                                borderTop: '4px solid var(--widget-main-color, #2563eb)',
                                borderRadius: 'var(--lc-radius-full)',
                                marginBottom: '20px',
                                animation: 'spin 1s linear infinite'
                            }}
                        ></div>
                        <span style={{
                            color: 'var(--lc-text-2, #6c757d)',
                            fontSize: '14px'
                        }}>{t('chat.loading_frame')}</span>
                    </div>
                )}

                {initialized && (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <style>{`
                            @keyframes spin {
                                from { transform: rotate(0deg); }
                                to { transform: rotate(360deg); }
                            }

                            .chat-frame-container .chat-container {
                                /* Flex child: fill the space ABOVE the input bar and
                                   scroll internally. Replaces the old height:100% +
                                   padding-bottom hack + absolute bar, which let the
                                   bar float over messages AND clipped the emoji/GIF
                                   picker (absolute, ignores padding). */
                                flex: 1 1 auto !important;
                                min-height: 0 !important;
                                box-sizing: border-box !important;
                                padding-bottom: var(--lc-space-2) !important;
                                overflow: auto !important;
                                -webkit-overflow-scrolling: touch !important;
                                /* 消息列表区背景：浅灰，与历史条带 / 工具栏色阶一致，避免大片白色 */
                                background: var(--lc-bg, #f8f9fa) !important;
                            }

                            /* Composer 容器统一管垂直节奏：底部留白只在这里定义一次
                               （powered-by 隐藏时间距不塌陷）；子元素不再各自带
                               min-height / 外边距叠加。 */
                            .chat-frame-container .mobile-area,
                            .chat-frame-container .pc-area {
                                position: relative !important;
                                flex-shrink: 0 !important;
                                max-height: ${isMobileBoolean ? '260px' : '240px'} !important;
                                display: flex !important;
                                flex-direction: column !important;
                                background: var(--lc-surface, #fff) !important;
                                border-top: var(--lc-hairline) !important;
                                z-index: 10 !important;
                                /* iPhone 手势条机型：inset:0 的面板贴底需要 safe-area 缓冲 */
                                padding-bottom: calc(${isMobileBoolean ? '8px' : '10px'} + env(safe-area-inset-bottom, 0px)) !important;
                            }

                            .chat-frame-container .msg-input {
                                position: relative !important;
                                bottom: auto !important;
                                left: auto !important;
                                right: auto !important;
                                flex: 0 0 auto !important;
                                display: flex !important;
                                align-items: center !important;
                                min-height: 0 !important;
                                padding: ${isMobileBoolean ? '6px 10px 0' : '8px 12px 0'} !important;
                                margin: 0 !important;
                            }

                            .chat-frame-container .toolbar {
                                position: relative !important;
                                display: flex !important;
                                align-items: center !important;
                                padding: ${isMobileBoolean ? '4px 10px' : '6px 12px'} !important;
                                border-bottom: var(--lc-hairline) !important;
                                background: var(--lc-bg, #f8f9fa) !important;
                                min-height: 0 !important;
                                flex-shrink: 0 !important;
                                margin: 0 !important;
                            }

                            /* Reply bar 样式修复
                               order:-1 把回复条放到 toolbar / msg-input / emoji-pick 之上方，
                               这样打开 emoji / GIF 弹层时不会盖住"取消回复"按钮，用户随时可关。 */
                            .chat-frame-container .reply-bar {
                                position: relative !important;
                                bottom: auto !important;
                                left: auto !important;
                                right: auto !important;
                                z-index: 999 !important;
                                /* 引用条：品牌色左描边(quote 风) + 极浅品牌底色，替代原来突兀的蓝色。 */
                                background-color: rgba(var(--widget-main-rgb, 37, 99, 235), 0.06) !important;
                                border-top: var(--lc-hairline) !important;
                                border-bottom: var(--lc-hairline) !important;
                                border-left: 3px solid var(--widget-main-color, #2563eb) !important;
                                padding: 7px 12px 7px 11px !important;
                                margin: 0 !important;
                                /* flex order 统一在下方"确保顺序"块里定义，避免两处冲突 */
                                flex-shrink: 0 !important;
                                animation: slideInFromBottom var(--lc-dur-slow) var(--lc-ease-out) !important;
                            }

                            .chat-frame-container .reply-bar:hover {
                                background-color: rgba(var(--widget-main-rgb, 37, 99, 235), 0.1) !important;
                            }

                            .chat-frame-container .reply-bar-content {
                                display: flex !important;
                                align-items: center !important;
                                gap: var(--lc-space-2) !important;
                            }

                            .chat-frame-container .reply-bar-icon {
                                flex-shrink: 0 !important;
                                color: var(--widget-main-color, #2563eb) !important;
                                display: inline-flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                            }

                            .chat-frame-container .reply-bar-text {
                                flex: 1 !important;
                                overflow: hidden !important;
                                min-width: 0 !important;
                            }

                            .chat-frame-container .reply-bar-from {
                                font-size: 12px !important;
                                color: var(--widget-main-color, #2563eb) !important;
                                font-weight: 600 !important;
                                margin-bottom: 2px !important;
                            }

                            .chat-frame-container .reply-bar-message {
                                font-size: 13px !important;
                                color: var(--lc-text-2, #666) !important;
                                white-space: nowrap !important;
                                overflow: hidden !important;
                                text-overflow: ellipsis !important;
                            }

                            .chat-frame-container .reply-bar-cancel {
                                background: none !important;
                                border: none !important;
                                color: var(--lc-text-3, #999) !important;
                                cursor: pointer !important;
                                font-size: 18px !important;
                                padding: 0 5px !important;
                                line-height: 1 !important;
                                flex-shrink: 0 !important;
                            }

                            .chat-frame-container .reply-bar-cancel:hover {
                                color: var(--lc-text-2, #666) !important;
                            }

                            /* 当有reply-bar时放宽容器上限（高度本身由内容决定） */
                            .chat-frame-container .mobile-area:has(.reply-bar),
                            .chat-frame-container .pc-area:has(.reply-bar) {
                                max-height: ${isMobileBoolean ? '300px' : '280px'} !important;
                            }

                            /* Reply bar 动画 */
                            @keyframes slideInFromBottom {
                                from {
                                    transform: translateY(20px);
                                    opacity: 0;
                                }
                                to {
                                    transform: translateY(0);
                                    opacity: 1;
                                }
                            }

                            /* 输入区自上而下：reply-bar → toolbar → msg-input。
                               回复条放在【工具栏上方】：这样它和"你正在回复 X"的语境
                               统领整个 composer，且 emoji/GIF 弹层(bottom 偏移)不会盖住
                               取消回复按钮。 */
                            .chat-frame-container .mobile-area,
                            .chat-frame-container .pc-area {
                                display: flex !important;
                                flex-direction: column !important;
                            }

                            .chat-frame-container .reply-bar {
                                order: 1 !important;
                            }

                            .chat-frame-container .toolbar {
                                order: 2 !important;
                            }

                            .chat-frame-container .msg-input {
                                order: 3 !important;
                            }

                            /* Emoji picker 和 GIF picker 样式修复 */
                            .chat-frame-container .emoji-pick {
                                position: absolute !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                /* 下边界 = 输入区实测高度（chat.js 用 ResizeObserver 写进
                                   --lc-composer-h）。写死的 116/124px 只当兜底：输入框长高
                                   或 Powered by 显隐时，魔数必然对不上（实测差 5px，弹层压住输入栏顶边）。 */
                                bottom: var(--lc-composer-h, ${isMobileBoolean ? '116px' : '124px'}) !important;
                                z-index: 9999 !important;
                                background: transparent !important;
                                border: none !important;
                                border-radius: 0 !important;
                                overflow: hidden !important;
                                height: auto !important;
                                width: 100% !important;
                            }
                            .emoji-pick > div:first-child { height: 100%; }
                            .chat-frame-container .emoji-pick > * {
                                /* 颜色专项:这两行原本是写死的 rgba(255,255,255,.95) +
                                   rgba(0,0,0,.1) 描边,暗色模式下 emoji/GIF 面板整块保持
                                   亮白 —— 而且带 !important,连主题覆写都盖不掉。改走
                                   --lc-emoji-bg-rgb(暗色下由 darkPaletteToCssVars 写成
                                   surface),描边走 --lc-border。半透明与 blur 保留:
                                   那是这块面板的既有质感,不是缺陷。 */
                                background: rgba(var(--lc-emoji-bg-rgb, 255, 255, 255), 0.95) !important;
                                backdrop-filter: blur(10px) !important;
                                border-radius: var(--lc-radius-md) !important;
                                border: var(--lc-hairline) !important;
                                box-shadow: var(--lc-shadow-3) !important;
                            }

                            .chat-frame-container .giphy {
                                position: absolute !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                bottom: var(--lc-composer-h, ${isMobileBoolean ? '116px' : '124px'}) !important; /* 同上：实测高度优先，魔数兜底 */
                                z-index: 9999 !important;
                                background: transparent !important;
                                border: none !important;
                                border-radius: 0 !important;
                                overflow: hidden !important;
                            }

                            .chat-frame-container .giphy > * {
                                /* 颜色专项:这两行原本是写死的 rgba(255,255,255,.95) +
                                   rgba(0,0,0,.1) 描边,暗色模式下 emoji/GIF 面板整块保持
                                   亮白 —— 而且带 !important,连主题覆写都盖不掉。改走
                                   --lc-emoji-bg-rgb(暗色下由 darkPaletteToCssVars 写成
                                   surface),描边走 --lc-border。半透明与 blur 保留:
                                   那是这块面板的既有质感,不是缺陷。 */
                                background: rgba(var(--lc-emoji-bg-rgb, 255, 255, 255), 0.95) !important;
                                backdrop-filter: blur(10px) !important;
                                border-radius: var(--lc-radius-md) !important;
                                border: var(--lc-hairline) !important;
                                box-shadow: var(--lc-shadow-3) !important;
                                height: 100% !important;
                            }

                            /* Toolbar 按钮样式增强 */
                            .chat-frame-container .toolbar a {
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                width: ${isMobileBoolean ? '44px' : '36px'} !important;
                                height: ${isMobileBoolean ? '44px' : '36px'} !important;
                                border-radius: var(--lc-radius-full) !important;
                                transition: background-color var(--lc-dur-base) var(--lc-ease-out) !important;
                                cursor: pointer !important;
                                margin: 0 var(--lc-space-1) !important;
                                position: relative !important;
                                touch-action: manipulation !important;
                                -webkit-tap-highlight-color: transparent !important;
                            }

                            .chat-frame-container .toolbar a:hover {
                                background-color: rgba(0, 0, 0, 0.05) !important;
                            }

                            .chat-frame-container .toolbar a:active {
                                background-color: rgba(0, 0, 0, 0.1) !important;
                                transform: scale(0.95) !important;
                            }

                            /* 通话按钮组：折叠成一个触发按钮，点击向右展开语音/视频/共享 */
                            .chat-frame-container .call-group {
                                display: inline-flex !important;
                                align-items: center !important;
                                flex-shrink: 0 !important;
                            }
                            .chat-frame-container .call-trigger.active {
                                color: var(--widget-main-color, #2563eb) !important;
                                background-color: rgba(var(--widget-main-rgb, 37, 99, 235), 0.1) !important;
                            }
                            .chat-frame-container .call-actions {
                                display: inline-flex !important;
                                align-items: center !important;
                                overflow: hidden !important;
                                max-width: 0 !important;
                                opacity: 0 !important;
                                transform: translateX(-6px) !important;
                                transition: max-width var(--lc-dur-slow) var(--lc-ease-out), opacity var(--lc-dur-base) var(--lc-ease-out), transform var(--lc-dur-slow) var(--lc-ease-out) !important;
                            }
                            .chat-frame-container .call-group.expanded .call-actions {
                                max-width: ${isMobileBoolean ? '210px' : '170px'} !important;
                                opacity: 1 !important;
                                transform: translateX(0) !important;
                            }

                            /* 图片上传组件样式 */
                            .chat-frame-container .uploadimg {
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                width: ${isMobileBoolean ? '44px' : '36px'} !important;
                                height: ${isMobileBoolean ? '44px' : '36px'} !important;
                                margin: 0 var(--lc-space-1) !important;
                                position: relative !important;
                            }

                            .chat-frame-container .uploadimg label {
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                width: 100% !important;
                                height: 100% !important;
                                cursor: pointer !important;
                                border-radius: var(--lc-radius-full) !important;
                                transition: background-color var(--lc-dur-base) var(--lc-ease-out) !important;
                                touch-action: manipulation !important;
                                -webkit-tap-highlight-color: transparent !important;
                                position: relative !important;
                                z-index: 10 !important;
                            }

                            .chat-frame-container .uploadimg label:hover {
                                background-color: rgba(0, 0, 0, 0.05) !important;
                            }

                            .chat-frame-container .uploadimg label:active {
                                background-color: rgba(0, 0, 0, 0.1) !important;
                                transform: scale(0.95) !important;
                            }

                            .chat-frame-container .fileInput {
                                width: 0.1px !important;
                                height: 0.1px !important;
                                opacity: 0 !important;
                                overflow: hidden !important;
                                position: absolute !important;
                                z-index: -1 !important;
                            }

                            /* 进度条样式 - 绝对定位在toolbar顶部 */
                            .chat-frame-container .progress-bar-container {
                                position: absolute !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                width: 100% !important;
                                /* 颜色专项:进度轨道原为写死浅灰,暗色下是一条亮杠。 */
                                background-color: var(--lc-surface-3) !important;
                                border-radius: 0 !important;
                                margin: 0 !important;
                                height: 3px !important;
                                overflow: hidden !important;
                                z-index: 1000 !important;
                                box-shadow: var(--lc-shadow-1) !important;
                            }

                            .chat-frame-container .progress-bar {
                                height: 100% !important;
                                /* R8:进度跟主色,不跟"成功绿" */
                                background: var(--lc-main-gradient) !important;
                                text-align: right !important;
                                line-height: 3px !important;
                                color: transparent !important;
                                font-size: 0 !important;
                                transition: width var(--lc-dur-slow) var(--lc-ease-out) !important;
                                min-width: 0 !important;
                                position: relative !important;
                                overflow: visible !important;
                            }

                            /* 进度条动画效果 */
                            .chat-frame-container .progress-bar::after {
                                content: '' !important;
                                position: absolute !important;
                                top: 0 !important;
                                right: 0 !important;
                                width: 20px !important;
                                height: 100% !important;
                                background: rgba(255, 255, 255, 0.3) !important;
                                animation: shimmer 1s infinite !important;
                            }

                            @keyframes shimmer {
                                0% { opacity: 0.5; }
                                50% { opacity: 1; }
                                100% { opacity: 0.5; }
                            }

                            /* 移动端特定优化 */
                            ${isMobileBoolean ? `
                            .chat-frame-container .toolbar {
                                padding: 4px 10px !important;
                                gap: var(--lc-space-1) !important;
                            }

                            .chat-frame-container .toolbar a {
                                min-width: 36px !important;
                                min-height: 36px !important;
                                width: 36px !important;
                                height: 36px !important;
                            }

                            .chat-frame-container .uploadimg {
                                min-width: 36px !important;
                                min-height: 36px !important;
                                width: 36px !important;
                                height: 36px !important;
                            }

                            /* 防止移动端点击穿透 */
                            .chat-frame-container .toolbar a,
                            .chat-frame-container .uploadimg label {
                                -webkit-touch-callout: none !important;
                                -webkit-user-select: none !important;
                                -khtml-user-select: none !important;
                                -moz-user-select: none !important;
                                -ms-user-select: none !important;
                                user-select: none !important;
                            }
                            ` : ``}

                            /* Textarea 唯一样式来源（已把 widget-index.js 的 .textarea 合并到此，消除两套 !important 打架）。
                               min-height 38px → 空态 1 行；实际高度由 chat.js adjustTextareaHeight 按 scrollHeight 设置。 */
                            .chat-frame-container .msg-input textarea {
                                flex: 1 !important;
                                border: var(--lc-hairline) !important;
                                border-radius: var(--lc-radius-full) !important;
                                background: var(--lc-surface-2, #f8fafc) !important;
                                color: var(--lc-text, #1e293b) !important;
                                padding: ${isMobileBoolean ? '9px 14px' : '9px 16px'} !important;
                                resize: none !important;
                                outline: none !important;
                                font-size: ${isMobileBoolean ? '16px' : '14px'} !important;
                                line-height: 18px !important;
                                min-height: 38px !important;
                                max-height: ${isMobileBoolean ? '162px' : '180px'} !important;
                                transition: height var(--lc-dur-fast) var(--lc-ease-out), border-color var(--lc-dur-base) var(--lc-ease-out), box-shadow var(--lc-dur-base) var(--lc-ease-out) !important;
                                overflow-y: hidden !important;
                                box-sizing: border-box !important;
                            }

                            .chat-frame-container .msg-input textarea:focus {
                                border-color: var(--widget-main-color, #2563eb) !important;
                                background: var(--lc-surface, #ffffff) !important;
                                box-shadow: 0 0 0 3px rgba(var(--widget-main-rgb, 37, 99, 235), 0.12) !important;
                            }

                            /* msg-input 高度完全由 textarea 内容驱动（adjustTextareaHeight），
                               不再用 min-height 撑底 —— 旧的 64px 强制高度正是"输入框
                               下方大空白"的来源。max-height 只做长文兜底。 */
                            .chat-frame-container .msg-input {
                                max-height: ${isMobileBoolean ? '180px' : '200px'} !important;
                            }

                            /* 根据设备类型优化 */
                            ${isMobileBoolean ? `
                            .chat-frame-container .msg-input textarea {
                                /* 移动端：支持3行到9行 (54px到162px) */
                                font-size: 16px !important; /* 防止iOS缩放 */
                            }
                            ` : `
                            .chat-frame-container .msg-input textarea {
                                /* PC端：支持3行到10行 (54px到180px) */
                                font-size: 14px !important;
                            }
                            `}
                        `}</style>
                        <div className="chat-frame-container" style={{height: '100%'}}>
                            <Chat
                                ref={this.chatRef}
                                {...chatProps}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
